import { apiFetch, apiPost, apiPut, apiDelete } from './api';
import type {
  AccessGrant,
  AccessLevel,
  ManagedUser,
  ProvisionUserPayload,
  UserAccess,
} from '@/types';

// Write/read side of user management. Endpoints live under /api/users (UserController);
// the per-resource grant routes key off the resource UUID (a "client" is a customer,
// served at /api/companies, so the customer grant uses the client's uuid). Client,
// project and contract grants are surfaced; folder grants exist server-side but are
// not surfaced here yet.

/**
 * Members of a client (anyone holding a grant on that customer), excluding deactivated
 * users. Server requires the caller to be ADMIN on the client (or a global admin).
 */
export async function listClientUsers(clientUuid: string): Promise<ManagedUser[]> {
  return apiFetch<ManagedUser[]>(`users?customerUuid=${clientUuid}`);
}

/** Every user in the system. Global-admin only server-side. */
export async function listAllUsers(): Promise<ManagedUser[]> {
  return apiFetch<ManagedUser[]>('users');
}

/**
 * Provision a user into a client: creates the user (role USER) if the email is new,
 * otherwise reuses the existing user, then upserts the client grant. Either way the
 * user ends up a member of the client at the requested level.
 */
export async function provisionUser(
  clientUuid: string,
  payload: ProvisionUserPayload
): Promise<ManagedUser> {
  return apiPost<ManagedUser, ProvisionUserPayload>(`users?customerUuid=${clientUuid}`, payload);
}

/** All grants held by a user across the resource hierarchy. */
export async function getUserAccess(userUuid: string): Promise<UserAccess> {
  return apiFetch<UserAccess>(`users/${userUuid}/access`);
}

/**
 * Deactivate (soft-delete) a user across the ENTIRE system: they can no longer sign in and drop
 * out of every member list, though their row is retained so historical authorship still resolves.
 * This is deliberately distinct from {@link revokeClientAccess}, which only removes a single client
 * membership and leaves the account intact. Global-admin only server-side; the server rejects (409)
 * deactivating yourself or a SYSTEMADMIN.
 */
export async function deactivateUser(userUuid: string): Promise<void> {
  return apiDelete(`users/${userUuid}`);
}

/** Grant or replace a user's access level on a client (customer). */
export async function grantClientAccess(
  userUuid: string,
  clientUuid: string,
  level: AccessLevel
): Promise<AccessGrant> {
  return apiPut<AccessGrant, { level: AccessLevel }>(
    `users/${userUuid}/access/customer/${clientUuid}`,
    { level }
  );
}

/**
 * Revoke a user's client membership. Cascades to their project/folder/contract grants
 * under that client server-side. Throws (409) when removing the client's last admin.
 */
export async function revokeClientAccess(userUuid: string, clientUuid: string): Promise<void> {
  return apiDelete(`users/${userUuid}/access/customer/${clientUuid}`);
}

/** Grant or replace a user's access level on a project. */
export async function grantProjectAccess(
  userUuid: string,
  projectUuid: string,
  level: AccessLevel
): Promise<AccessGrant> {
  return apiPut<AccessGrant, { level: AccessLevel }>(
    `users/${userUuid}/access/project/${projectUuid}`,
    { level }
  );
}

/** Revoke a user's access on a project. */
export async function revokeProjectAccess(userUuid: string, projectUuid: string): Promise<void> {
  return apiDelete(`users/${userUuid}/access/project/${projectUuid}`);
}

/** Grant or replace a user's access level on a single contract. */
export async function grantContractAccess(
  userUuid: string,
  contractUuid: string,
  level: AccessLevel
): Promise<AccessGrant> {
  return apiPut<AccessGrant, { level: AccessLevel }>(
    `users/${userUuid}/access/contract/${contractUuid}`,
    { level }
  );
}

/** Revoke a user's access on a single contract. */
export async function revokeContractAccess(userUuid: string, contractUuid: string): Promise<void> {
  return apiDelete(`users/${userUuid}/access/contract/${contractUuid}`);
}

/**
 * The caller's own access level on a client, returned as a bare level string. Used to
 * decide whether the current user can administer the client's members (ADMIN), since
 * there is no server endpoint that enumerates "clients I administer".
 */
export async function getClientAccessLevel(clientUuid: string): Promise<AccessLevel> {
  return apiFetch<AccessLevel>(`companies/${clientUuid}/access-level`);
}

/**
 * The caller's own resolved access level on a project, returned as a bare level string. The
 * server resolves this with inheritance (global admin → direct project grant → inherited client
 * grant), so ADMIN here means a global admin, a project admin, or a client admin of the project's
 * client. Used to gate project-admin-only actions (e.g. creating folders) in the UI.
 */
export async function getProjectAccessLevel(projectUuid: string): Promise<AccessLevel> {
  return apiFetch<AccessLevel>(`projects/${projectUuid}/access-level`);
}

/**
 * The caller's own resolved access level on a single contract, returned as a bare level string.
 * The server resolves this with the FULL cascade (global admin → direct contract grant → inherited
 * folder → project → client grant), exactly matching the @contractAuth checks that gate the
 * contract's write endpoints. Used to set the contract viewer read-only for a READER (every
 * mutation — add/edit/remove clause, review, flag, comment — requires EDITOR+). Returns 403 if the
 * caller has no access at all (handle as "no edit access").
 */
export async function getContractAccessLevel(contractUuid: string): Promise<AccessLevel> {
  return apiFetch<AccessLevel>(`contracts/${contractUuid}/access-level`);
}

export const userManagementService = {
  listClientUsers,
  listAllUsers,
  provisionUser,
  getUserAccess,
  deactivateUser,
  grantClientAccess,
  revokeClientAccess,
  grantProjectAccess,
  revokeProjectAccess,
  grantContractAccess,
  revokeContractAccess,
  getClientAccessLevel,
  getProjectAccessLevel,
  getContractAccessLevel,
};
